import { useEffect, useState } from "react";
import { Loader2, LogOut, Inbox, Image as ImageIcon, Trash2, Upload } from "lucide-react";
import Seo from "@/components/Seo";
import api, { formatApiError } from "@/lib/api";
import { COMPANY } from "@/data/site";
import { ArticlesTab } from "@/components/admin/ArticlesTab";
import { VacanciesTab } from "@/components/admin/VacanciesTab";
import { JobApplicationsTab } from "@/components/admin/JobApplicationsTab";
import { PodcastsTab } from "@/components/admin/PodcastsTab";

const STATUSES = ["NEW", "CONTACTED", "QUOTED", "NEGOTIATION", "WON", "LOST"];

const STATUS_STYLES = {
  NEW: "bg-cts-redlight text-cts-red",
  CONTACTED: "bg-cts-soft text-cts-charcoal",
  QUOTED: "bg-cts-soft text-cts-charcoal",
  NEGOTIATION: "bg-cts-soft text-cts-charcoal",
  WON: "bg-green-100 text-green-800",
  LOST: "bg-cts-soft text-cts-charcoal/60",
};

const MEDIA_SLOTS = {
  heroPc: "Homepage Hero — Assembled PC",
  heroTech: "Computer & Laptop Service",
  workspace: "Assembled PCs / Installation",
  circuit: "Components & Accessories",
  network: "Networking",
  cctv: "CCTV & Surveillance",
  printer: "Printers",
  toner: "Toners & Consumables",
  ups: "UPS & Power",
  solar: "Solar Energy Solutions",
  office: "Offices",
  hospital: "Hospitals & Clinics",
  bank: "Banks & Financial Offices",
  smallbiz: "Small Business",
  institution: "Institutions",
};

const MediaTab = () => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [slotKey, setSlotKey] = useState("heroPc");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/media");
      setMedia(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onUpload = async (e) => {
    e.preventDefault();
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const form = new FormData();
      form.append("slot_key", slotKey);
      form.append("file", file);
      await api.post("/admin/media/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      setFile(null);
      load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async (id) => {
    await api.delete(`/admin/media/${id}`);
    load();
  };

  const currentBySlot = Object.fromEntries(media.map((m) => [m.slot_key, m]));

  return (
    <div data-testid="admin-media-tab">
      <form onSubmit={onUpload} className="rounded-xl border border-cts-line bg-white p-6 mb-8 grid gap-4 sm:grid-cols-[1fr_1fr_auto] items-end" data-testid="media-upload-form">
        <div>
          <label className="field-label">Image Slot</label>
          <select value={slotKey} onChange={(e) => setSlotKey(e.target.value)} data-testid="media-slot-select" className="field-input">
            {Object.entries(MEDIA_SLOTS).map(([k, label]) => (
              <option key={k} value={k}>{label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="field-label">Image File</label>
          <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="media-file-input" className="field-input !pt-3" />
        </div>
        <button type="submit" disabled={!file || uploading} data-testid="media-upload-btn" className="btn-primary disabled:opacity-60">
          {uploading ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />} UPLOAD
        </button>
        {error && <p className="sm:col-span-3 text-sm text-cts-red" data-testid="media-upload-error">{error}</p>}
      </form>

      {loading ? (
        <div className="text-center py-10"><Loader2 className="animate-spin mx-auto text-cts-red" size={24} /></div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Object.entries(MEDIA_SLOTS).map(([k, label]) => {
            const item = currentBySlot[k];
            return (
              <div key={k} className="rounded-lg border border-cts-line bg-white overflow-hidden" data-testid={`media-slot-${k}`}>
                <div className="h-32 bg-cts-soft flex items-center justify-center overflow-hidden">
                  {item ? (
                    <img src={item.url} alt={label} className="w-full h-full object-cover" />
                  ) : (
                    <ImageIcon size={28} className="text-cts-charcoal/30" />
                  )}
                </div>
                <div className="p-3 flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold text-cts-ink">{label}</span>
                  {item && (
                    <button onClick={() => onDelete(item.id)} data-testid={`media-delete-${k}`} className="text-cts-charcoal/60 hover:text-cts-red" aria-label={`Remove image for ${label}`}>
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default function Admin() {
  const [user, setUser] = useState(undefined); // undefined = checking
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [leads, setLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [filter, setFilter] = useState("");
  const [tab, setTab] = useState("leads");

  const loadLeads = async () => {
    setLoadingLeads(true);
    try {
      const { data } = await api.get("/leads", { params: filter ? { form_type: filter } : {} });
      setLeads(data);
    } catch (e) {
      if (e.response?.status === 401) setUser(null);
    } finally {
      setLoadingLeads(false);
    }
  };

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => setUser(data)).catch(() => setUser(null));
  }, []);

  useEffect(() => {
    if (user) loadLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, filter]);

  const onLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    setLoginError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
    } catch (err) {
      setLoginError(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoggingIn(false);
    }
  };

  const onLogout = async () => {
    await api.post("/auth/logout");
    setUser(null);
  };

  const updateStatus = async (id, status) => {
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
    try {
      await api.patch(`/leads/${id}`, { status });
    } catch (e) {
      loadLeads();
    }
  };

  if (user === undefined) {
    return (
      <div className="section-pad container-site text-center" data-testid="admin-loading">
        <Loader2 className="animate-spin mx-auto text-cts-red" size={32} />
      </div>
    );
  }

  if (!user) {
    return (
      <>
        <Seo title={`Admin Login | ${COMPANY.brand}`} siteName={COMPANY.brand} description="CTS admin login." noindex />
        <section className="section-pad bg-cts-soft">
          <div className="container-site">
            <div className="max-w-sm mx-auto rounded-xl border border-cts-line bg-white p-8" data-testid="admin-login-card">
              <img src="/cts-logo.png" alt="Cherians Tech Solutions logo" className="h-12 w-auto mb-6" />
              <h1 className="h3-display mb-6">Admin Login</h1>
              <form onSubmit={onLogin} className="space-y-4">
                <div>
                  <label htmlFor="admin-email" className="field-label">Email</label>
                  <input id="admin-email" data-testid="admin-email-input" type="email" required className="field-input" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" />
                </div>
                <div>
                  <label htmlFor="admin-password" className="field-label">Password</label>
                  <input id="admin-password" data-testid="admin-password-input" type="password" required className="field-input" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
                </div>
                {loginError && (
                  <p className="rounded-md bg-cts-redlight px-4 py-3 text-sm text-cts-ink" role="alert" data-testid="admin-login-error">{loginError}</p>
                )}
                <button type="submit" data-testid="admin-login-btn" disabled={loggingIn} className="btn-primary w-full disabled:opacity-60">
                  {loggingIn ? <Loader2 className="animate-spin" size={18} /> : "LOG IN"}
                </button>
              </form>
            </div>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <Seo title={`Admin Dashboard | ${COMPANY.brand}`} siteName={COMPANY.brand} description="CTS admin dashboard." noindex />
      <section className="section-pad bg-cts-soft min-h-[60vh]">
        <div className="container-site" data-testid="admin-dashboard">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div>
              <p className="eyebrow mb-1">CTS ADMIN</p>
              <h1 className="h2-display">{tab === "leads" ? "Website Leads" : tab === "media" ? "Media Library" : tab === "articles" ? "Technology Advice" : tab === "vacancies" ? "Vacancies" : tab === "podcasts" ? "Podcasts" : "Job Applications"}</h1>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex rounded-md border border-cts-line bg-white overflow-hidden">
                <button onClick={() => setTab("leads")} data-testid="admin-tab-leads" className={`px-4 py-2 text-xs font-heading font-bold uppercase ${tab === "leads" ? "bg-cts-red text-white" : "text-cts-charcoal"}`}>Leads</button>
                <button onClick={() => setTab("media")} data-testid="admin-tab-media" className={`px-4 py-2 text-xs font-heading font-bold uppercase ${tab === "media" ? "bg-cts-red text-white" : "text-cts-charcoal"}`}>Media</button>
                <button onClick={() => setTab("articles")} data-testid="admin-tab-articles" className={`px-4 py-2 text-xs font-heading font-bold uppercase ${tab === "articles" ? "bg-cts-red text-white" : "text-cts-charcoal"}`}>Technology Advice</button>
                <button onClick={() => setTab("podcasts")} data-testid="admin-tab-podcasts" className={`px-4 py-2 text-xs font-heading font-bold uppercase ${tab === "podcasts" ? "bg-cts-red text-white" : "text-cts-charcoal"}`}>Podcasts</button>
                <button onClick={() => setTab("vacancies")} data-testid="admin-tab-vacancies" className={`px-4 py-2 text-xs font-heading font-bold uppercase ${tab === "vacancies" ? "bg-cts-red text-white" : "text-cts-charcoal"}`}>Vacancies</button>
                <button onClick={() => setTab("job-applications")} data-testid="admin-tab-job-applications" className={`px-4 py-2 text-xs font-heading font-bold uppercase ${tab === "job-applications" ? "bg-cts-red text-white" : "text-cts-charcoal"}`}>Job Applications</button>
              </div>
              {tab === "leads" && (
                <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="admin-filter-select" className="field-input !w-auto" aria-label="Filter by form type">
                  <option value="">All types</option>
                  {["quote", "contact", "service", "toner", "printer", "pc-config", "business", "amc"].map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              )}
              <button onClick={onLogout} data-testid="admin-logout-btn" className="btn-outline !min-h-[44px] !py-2">
                <LogOut size={16} /> LOG OUT
              </button>
            </div>
          </div>

          {tab === "media" ? (
            <MediaTab />
          ) : tab === "articles" ? (
            <ArticlesTab />
          ) : tab === "podcasts" ? (
            <PodcastsTab />
          ) : tab === "vacancies" ? (
            <VacanciesTab />
          ) : tab === "job-applications" ? (
            <JobApplicationsTab />
          ) : loadingLeads ? (
            <div className="text-center py-16"><Loader2 className="animate-spin mx-auto text-cts-red" size={28} /></div>
          ) : leads.length === 0 ? (
            <div className="rounded-xl border border-cts-line bg-white p-14 text-center" data-testid="admin-empty">
              <Inbox size={36} className="mx-auto text-cts-red mb-3" strokeWidth={1.5} />
              <p className="font-heading font-bold text-cts-ink">No leads yet</p>
              <p className="text-sm text-cts-charcoal mt-1">New website enquiries will appear here.</p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-cts-line bg-white" data-testid="admin-leads-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-cts-line text-left">
                    {["Date", "Name", "Phone", "Type", "Requirement", "Location", "Message / Details", "Status"].map((h) => (
                      <th key={h} className="px-4 py-3 font-heading font-bold text-xs uppercase tracking-wide text-cts-charcoal">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead) => (
                    <tr key={lead.id} className="border-b border-cts-line last:border-0 align-top" data-testid={`lead-row-${lead.id}`}>
                      <td className="px-4 py-3 whitespace-nowrap text-cts-charcoal">{new Date(lead.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</td>
                      <td className="px-4 py-3 font-semibold text-cts-ink">{lead.name}{lead.organization && <span className="block text-xs font-normal text-cts-charcoal/70">{lead.organization}</span>}</td>
                      <td className="px-4 py-3 whitespace-nowrap"><a href={`tel:${lead.phone}`} className="text-cts-red hover:underline">{lead.phone}</a></td>
                      <td className="px-4 py-3">
                        <span className="rounded bg-cts-soft px-2 py-1 text-xs font-semibold text-cts-charcoal">{lead.form_type}</span>
                        {lead.source_page && <span className="block mt-1 text-[10px] text-cts-charcoal/60">{lead.source_page}</span>}
                      </td>
                      <td className="px-4 py-3">{lead.requirement}</td>
                      <td className="px-4 py-3">{lead.location || "—"}</td>
                      <td className="px-4 py-3 max-w-[260px]">
                        {lead.message && <p className="text-cts-charcoal">{lead.message}</p>}
                        {lead.details && Object.keys(lead.details).length > 0 && (
                          <p className="text-xs text-cts-charcoal/70 mt-1">
                            {Object.entries(lead.details).map(([k, v]) => `${k}: ${v}`).join(" · ")}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <select
                          value={lead.status}
                          onChange={(e) => updateStatus(lead.id, e.target.value)}
                          data-testid={`lead-status-${lead.id}`}
                          aria-label={`Status for lead from ${lead.name}`}
                          className={`rounded px-2 py-1.5 text-xs font-bold border-0 cursor-pointer ${STATUS_STYLES[lead.status] || ""}`}
                        >
                          {STATUSES.map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
